import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Video, MapPin, User, X, AlertCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { formatTime } from '../../utils/dateUtils';
import { CurrencyIcon } from '../../utils/currencyIcon';
import { publicBookingAPI } from '../../services/api';
import { initializeRazorpayCheckout } from '../../utils/razorpayHelper';

const PublicBookingModal = ({ slot, partnerName, partnerId, feeSettings, therapyType, onConfirm, onCancel, loading, mcid }) => {
  const navigate = useNavigate();

  // Step: 'form' | 'account-setup'
  const [step, setStep] = useState('form');

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    sex: 'Male',
    location: '',
    contact: '',
    whatsapp_number: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [countryCode, setCountryCode] = useState('+91');
  const [whatsappCountryCode, setWhatsappCountryCode] = useState('+91');
  const [sameAsContact, setSameAsContact] = useState(false);

  const [passwordData, setPasswordData] = useState({ password: '', confirmPassword: '' });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const isOnlineOnly = slot?.status === 'available_online';
  const isOfflineOnly = slot?.status === 'available_offline';
  const isForced = isOnlineOnly || isOfflineOnly;
  const forcedType = isOnlineOnly ? 'online' : isOfflineOnly ? 'offline' : '';
  const [sessionType, setSessionType] = useState(forcedType);
  const modalRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (slot) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const scrollY = window.scrollY;

      document.body.style.overflow = 'hidden';
      if (window.innerWidth <= 768) {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
      }

      const timer = setTimeout(() => {
        if (backdropRef.current) {
          backdropRef.current.scrollTop = 0;
          backdropRef.current.scrollTo({ top: 0, behavior: 'auto' });
        }
        if (window.scrollY > 0 && window.innerWidth > 768) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 10);

      return () => {
        clearTimeout(timer);
        document.body.style.overflow = originalOverflow;
        if (window.innerWidth <= 768) {
          document.body.style.position = originalPosition;
          document.body.style.top = '';
          document.body.style.width = '';
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [slot]);

  if (!slot) return null;

  const formattedDate = new Date(slot.slot_date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const isOnline = sessionType === 'online';

  const sessionFee = therapyType ? (parseFloat(therapyType.fee) || 0) : (parseFloat(feeSettings?.session_fee) || 0);
  const currency = therapyType ? (therapyType.currency || 'INR') : (feeSettings?.fee_currency || 'INR');

  const getCurrencySymbol = (curr) => {
    const symbols = { 'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AUD': 'A$', 'CAD': 'C$' };
    return symbols[curr] || curr;
  };
  const currencySymbol = getCurrencySymbol(currency);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'contact' && sameAsContact) {
      setFormData(prev => ({ ...prev, whatsapp_number: value }));
      setWhatsappCountryCode(countryCode);
    }
  };

  const handleSameAsContactChange = (e) => {
    const checked = e.target.checked;
    setSameAsContact(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, whatsapp_number: prev.contact }));
      setWhatsappCountryCode(countryCode);
    } else {
      setFormData(prev => ({ ...prev, whatsapp_number: '' }));
    }
  };

  const handleCountryCodeChange = (e) => {
    const newCode = e.target.value;
    setCountryCode(newCode);
    if (sameAsContact) setWhatsappCountryCode(newCode);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.age) {
      newErrors.age = 'Age is required';
    } else {
      const ageNum = parseInt(formData.age);
      if (isNaN(ageNum) || ageNum < 1 || ageNum > 150) newErrors.age = 'Please enter a valid age (1-150)';
    }
    if (!formData.contact.trim()) {
      newErrors.contact = 'Contact number is required';
    } else if (!/^\d{7,15}$/.test(formData.contact.trim())) {
      newErrors.contact = 'Please enter a valid phone number (7-15 digits)';
    }
    if (formData.whatsapp_number.trim() && !/^\d{7,15}$/.test(formData.whatsapp_number.trim())) {
      newErrors.whatsapp_number = 'Please enter a valid WhatsApp number (7-15 digits)';
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!isForced && !sessionType) newErrors.sessionType = 'Session type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!passwordData.password) {
      newErrors.password = 'Password is required';
    } else if (passwordData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.password !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1 submit: validate form, move to account-setup step
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const clientData = {
      name: formData.name.trim(),
      age: parseInt(formData.age),
      sex: formData.sex,
      location: formData.location.trim() || null,
      contact: `${countryCode}${formData.contact.trim()}`,
      whatsapp_number: formData.whatsapp_number.trim() ? `${whatsappCountryCode}${formData.whatsapp_number.trim()}` : null,
      email: formData.email.trim() || null,
      session_type: sessionType
    };

    // Free bookings skip account setup and go directly
    if (sessionFee <= 0) {
      onConfirm(clientData);
      return;
    }

    setStep('account-setup');
  };

  // Step 2 submit: validate password, run payment flow
  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    const clientData = {
      name: formData.name.trim(),
      age: parseInt(formData.age),
      sex: formData.sex,
      location: formData.location.trim() || null,
      contact: `${countryCode}${formData.contact.trim()}`,
      whatsapp_number: formData.whatsapp_number.trim() ? `${whatsappCountryCode}${formData.whatsapp_number.trim()}` : null,
      email: formData.email.trim() || null,
      session_type: sessionType
    };

    setPaymentLoading(true);
    try {
      const options = { session_type: sessionType };
      if (therapyType?.therapy_type) options.therapy_type = therapyType.therapy_type;
      if (mcid) options.mcid = mcid;

      const orderResponse = await publicBookingAPI.createBookingOrder(partnerId, slot.id, clientData, options);

      // Test mode
      if (orderResponse.data.skip_payment || orderResponse.data.test_mode) {
        const verifyPayload = {
          razorpay_order_id: null,
          razorpay_payment_id: null,
          razorpay_signature: null,
          slot_id: slot.id,
          partner_id: partnerId,
          clientData,
          session_type: sessionType,
          mcid: mcid || null,
          password: passwordData.password
        };

        const verifyResponse = await publicBookingAPI.verifyBookingPayment(verifyPayload);

        if (verifyResponse.data.booking_confirmed) {
          navigate('/payment-success', {
            state: {
              payment: verifyResponse.data.payment || {
                id: `test_payment_${Date.now()}`,
                status: 'captured',
                amount: 0,
                currency,
                razorpay_payment_id: null
              },
              booking: {
                ...verifyResponse.data.booking,
                slot_id: slot.id,
                is_public_booking: true
              }
            },
            replace: true
          });
        } else {
          throw new Error('Booking verification failed');
        }
        return;
      }

      // Normal Razorpay flow
      const order = orderResponse.data.order;
      const paymentResult = await initializeRazorpayCheckout(order, {
        name: clientData.name,
        description: 'Session Fee Payment',
        prefill: {
          name: clientData.name,
          email: clientData.email || '',
          contact: clientData.contact
        }
      });

      const verifyPayload = {
        razorpay_order_id: paymentResult.razorpay_order_id,
        razorpay_payment_id: paymentResult.razorpay_payment_id,
        razorpay_signature: paymentResult.razorpay_signature,
        session_type: sessionType,
        mcid: mcid || null,
        password: passwordData.password
      };

      const verifyResponse = await publicBookingAPI.verifyBookingPayment(verifyPayload);

      if (verifyResponse.data.booking_confirmed) {
        navigate('/payment-success', {
          state: {
            payment: verifyResponse.data.payment,
            booking: {
              ...verifyResponse.data.booking,
              slot_id: slot.id,
              is_public_booking: true
            }
          },
          replace: true
        });
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      if (error.message === 'Payment cancelled by user') {
        alert('Payment cancelled. Your booking was not completed.');
      } else {
        console.error('Booking error:', error);
        alert(error.response?.data?.error || 'Failed to process booking. Please try again.');
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  // ─── Shared slot info box ────────────────────────────────────────────────
  const SlotInfo = () => (
    <div className="bg-blue-50 dark:bg-dark-bg-secondary border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center">
        <User className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
        <div>
          <p className="text-xs text-gray-600 dark:text-dark-text-secondary">Therapist</p>
          <p className="font-semibold text-gray-900 dark:text-dark-text-primary">{partnerName}</p>
        </div>
      </div>
      <div className="flex items-center">
        <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
        <div>
          <p className="text-xs text-gray-600 dark:text-dark-text-secondary">Date</p>
          <p className="font-semibold text-gray-900 dark:text-dark-text-primary">{formattedDate}</p>
        </div>
      </div>
      <div className="flex items-center">
        <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
        <div>
          <p className="text-xs text-gray-600 dark:text-dark-text-secondary">Time</p>
          <p className="font-semibold text-gray-900 dark:text-dark-text-primary">
            {formatTime(slot.start_datetime)} - {formatTime(slot.end_datetime)}
          </p>
        </div>
      </div>
      <div className="flex items-center">
        {isOnline ? (
          <Video className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
        ) : (
          <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
        )}
        <div>
          <p className="text-xs text-gray-600 dark:text-dark-text-secondary">Session Type</p>
          <p className="font-semibold text-gray-900 dark:text-dark-text-primary">
            {isOnline ? 'Online Session' : 'In-Person Session'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-4 sm:pt-8 pb-4 sm:pb-8 overflow-y-auto"
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-dark-bg-primary rounded-lg shadow-xl max-w-2xl w-full max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] flex flex-col"
      >
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="bg-primary-50 dark:bg-dark-bg-secondary border-b border-primary-200 dark:border-dark-border px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-dark-text-primary">
              {step === 'form' ? 'Book Appointment' : 'Set Up Your Account'}
            </h3>
            {step === 'form' && (
              <button
                onClick={onCancel}
                className="text-primary-600 dark:text-dark-primary-400 hover:text-primary-800 dark:hover:text-dark-primary-300 transition-colors"
                disabled={loading}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          {step === 'account-setup' && (
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary mt-1">
              Create a password so you can log in and manage your appointments.
            </p>
          )}
        </div>

        {/* ── Step 1: Booking Form ─────────────────────────────────────── */}
        {step === 'form' && (
          <form onSubmit={handleFormSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
              <SlotInfo />

              {/* Client Information */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary">Your Information</h4>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-dark-border'}`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{errors.name}</p>}
                </div>

                {/* Age & Sex */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                      Age <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      min="1"
                      max="150"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary ${errors.age ? 'border-red-500' : 'border-gray-300 dark:border-dark-border'}`}
                      placeholder="Age"
                    />
                    {errors.age && <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{errors.age}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                      Sex <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="sex"
                      value={formData.sex}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    Location <span className="text-gray-500">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                    placeholder="City, State"
                  />
                </div>

                {/* Contact */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    📞 Contact Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={countryCode}
                      onChange={handleCountryCodeChange}
                      className="px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                    >
                      <option value="+91">+91 (IN)</option>
                      <option value="+1">+1 (US/CA)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+61">+61 (AU)</option>
                    </select>
                    <input
                      type="tel"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary ${errors.contact ? 'border-red-500' : 'border-gray-300 dark:border-dark-border'}`}
                      placeholder="Phone number"
                      required
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-600 dark:text-dark-text-tertiary">Required. We'll use this to reach you if needed.</p>
                  {errors.contact && <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{errors.contact}</p>}
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    💬 WhatsApp Number
                  </label>
                  <div className="flex gap-2 mb-2">
                    <select
                      value={whatsappCountryCode}
                      onChange={(e) => setWhatsappCountryCode(e.target.value)}
                      disabled={sameAsContact}
                      className="px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="+91">+91 (IN)</option>
                      <option value="+1">+1 (US/CA)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+61">+61 (AU)</option>
                    </select>
                    <input
                      type="tel"
                      name="whatsapp_number"
                      value={formData.whatsapp_number}
                      onChange={handleChange}
                      disabled={sameAsContact}
                      className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary disabled:opacity-50 disabled:cursor-not-allowed ${errors.whatsapp_number ? 'border-red-500' : 'border-gray-300 dark:border-dark-border'}`}
                      placeholder="WhatsApp number"
                    />
                  </div>
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id="sameAsContact"
                      checked={sameAsContact}
                      onChange={handleSameAsContactChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="sameAsContact" className="ml-2 text-sm text-gray-700 dark:text-dark-text-secondary">
                      ☑ Same as contact number
                    </label>
                  </div>
                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                    <p className="text-xs font-semibold text-blue-800 dark:text-blue-300">
                      💡 Used for bookings, appointments, updates, reminders and quick communication
                    </p>
                  </div>
                  {errors.whatsapp_number && <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{errors.whatsapp_number}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    📧 Email Address <span className="text-gray-500">(Optional)</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary ${errors.email ? 'border-red-500' : 'border-gray-300 dark:border-dark-border'}`}
                    placeholder="your.email@example.com"
                  />
                  <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                    <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                      💡 Used only for account access and rarely for important notifications
                    </p>
                  </div>
                  {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{errors.email}</p>}
                </div>
              </div>

              {/* Session Type */}
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-dark-bg-secondary border border-yellow-200 dark:border-yellow-800 rounded-md">
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-2">
                  Session Type {!isForced && '*'}
                </label>
                {isForced ? (
                  <p className="font-semibold text-gray-900 dark:text-dark-text-primary">
                    {forcedType === 'online' ? 'Online Session (Video Call)' : 'In-Person Session (Office Visit)'}
                  </p>
                ) : (
                  <>
                    <select
                      value={sessionType}
                      onChange={(e) => setSessionType(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary ${errors.sessionType ? 'border-red-500' : 'border-gray-300 dark:border-dark-border'}`}
                      required
                    >
                      <option value="">-- Please select session type --</option>
                      <option value="online">Online Session (Video Call)</option>
                      <option value="offline">In-Person Session (Office Visit)</option>
                    </select>
                    {errors.sessionType && <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{errors.sessionType}</p>}
                  </>
                )}
              </div>

              {/* Fee */}
              {sessionFee > 0 && (
                <div className="bg-green-50 dark:bg-dark-bg-secondary border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-3 flex items-center">
                    <CurrencyIcon className="h-4 w-4 mr-2" />
                    Session Fee
                  </h4>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700 dark:text-dark-text-secondary">Total Amount to Pay:</span>
                    <span className="text-lg font-bold text-green-700 dark:text-green-400">
                      {currencySymbol}{sessionFee.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-dark-bg-secondary px-6 py-4 border-t border-gray-200 dark:border-dark-border flex-shrink-0">
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : sessionFee > 0 ? 'Next' : 'Confirm Booking'}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* ── Step 2: Account Setup ────────────────────────────────────── */}
        {step === 'account-setup' && (
          <form onSubmit={handleProceedToPayment} className="flex flex-col flex-1 min-h-0">
            <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
              <SlotInfo />

              {/* Info banner */}
              <div className="bg-blue-50 dark:bg-dark-bg-primary border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Set a password below so you can log in and manage your appointments after payment.
                </p>
              </div>

              {/* Username (read-only) */}
              {(formData.email || formData.whatsapp_number || formData.contact) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                    <User className="inline h-4 w-4 mr-1" />
                    Username (Login ID)
                  </label>
                  <input
                    type="text"
                    value={
                      formData.email.trim()
                        ? formData.email.trim()
                        : formData.whatsapp_number.trim()
                        ? `${whatsappCountryCode}${formData.whatsapp_number.trim()}`
                        : `${countryCode}${formData.contact.trim()}`
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md bg-gray-50 dark:bg-dark-bg-primary text-gray-700 dark:text-dark-text-secondary cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-tertiary">
                    You'll use this to log in to your account
                  </p>
                </div>
              )}

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordData.password}
                    onChange={(e) => {
                      setPasswordData(p => ({ ...p, password: e.target.value }));
                      if (passwordErrors.password) setPasswordErrors(p => ({ ...p, password: '' }));
                    }}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-primary dark:text-dark-text-primary ${passwordErrors.password ? 'border-red-500' : 'border-gray-300 dark:border-dark-border'}`}
                    placeholder="Enter password (min. 6 characters)"
                    required
                    disabled={paymentLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-dark-text-tertiary"
                    disabled={paymentLoading}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {passwordErrors.password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />{passwordErrors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => {
                      setPasswordData(p => ({ ...p, confirmPassword: e.target.value }));
                      if (passwordErrors.confirmPassword) setPasswordErrors(p => ({ ...p, confirmPassword: '' }));
                    }}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-primary dark:text-dark-text-primary ${passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-dark-border'}`}
                    placeholder="Confirm your password"
                    required
                    disabled={paymentLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-dark-text-tertiary"
                    disabled={paymentLoading}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />{passwordErrors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Fee reminder */}
              {sessionFee > 0 && (
                <div className="bg-green-50 dark:bg-dark-bg-secondary border border-green-200 dark:border-green-800 rounded-lg p-3 flex justify-between items-center">
                  <span className="text-sm text-gray-700 dark:text-dark-text-secondary flex items-center gap-1">
                    <CurrencyIcon className="h-4 w-4" /> Amount to Pay:
                  </span>
                  <span className="text-lg font-bold text-green-700 dark:text-green-400">
                    {currencySymbol}{sessionFee.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-dark-bg-secondary px-6 py-4 border-t border-gray-200 dark:border-dark-border flex-shrink-0">
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setStep('form')}
                  disabled={paymentLoading}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={paymentLoading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {paymentLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      Proceed to Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PublicBookingModal;
